import { insertTrackEvent } from '@comeon/analytics';
import reducerRegistry from '@comeon/reducer-registry';
import { hideBinaryNotification, showNotification } from '~/components/notifications/ducks/notifications.js';
import { requestApi } from '~/services/api.js';
import currencyFormatter from '~/libs/currency-formatter.js';
import REDUCER_NAME from './reducer-name.js';
import { DL_TYPE_DAY, DL_TYPE_WEEK, DL_TYPE_MONTH } from '../constants';
import { LICENSE_OGA } from '~/utils/license-utils.js';
import { timeLimitRefetch } from '~/components/account/time-limit/ducks';
import date from 'date-and-time';
import { sendAppBridgeEvent, WEB_EVENTS } from '~/components/app-bridge';

export const GET_LIMITS = '@komigen/mobile-frontend/limits/GET_LIMITS';
export const GET_DEPOSIT_LIMITS = '@komigen/mobile-frontend/limits/GET_DEPOSIT_LIMITS';
export const EDIT_LIMIT = '@komigen/mobile-frontend/limits/EDIT_LIMIT';
export const UPDATE_LIMIT = '@komigen/mobile-frontend/limits/UPDATE_LIMIT';
export const REMOVE_LIMIT = '@komigen/mobile-frontend/limits/REMOVE_LIMIT';
export const REMOVE_ALL_LIMITS = '@komigen/mobile-frontend/limits/REMOVE_ALL_LIMITS';
export const RESET_LIMITS = '@komigen/mobile-frontend/limits/RESET_LIMITS';
export const REMOVE_LIMIT_UPDATE_REQUEST = '@komigen/mobile-frontend/limits/REMOVE_LIMIT_UPDATE_REQUEST';
export const GET_REALITY_CHECK_DURATION = '@komigen/mobile-frontend/limits/GET_REALITY_CHECK_DURATION';
export const INITIALIZE_DEPOSIT_LIMITS = '@komigen/mobile-frontend/limits/INITIALIZE_DEPOSIT_LIMITS';
export const UPDATE_DEPOSIT_LIMIT = '@komigen/mobile-frontend/limits/UPDATE_DEPOSIT_LIMIT';
export const SHOW_LOSS_LIMIT_MESSAGE = '@komigen/mobile-frontend/limits/SHOW_LOSS_LIMIT_MESSAGE';
const SHOW_SPINNER = '@komigen/mobile-frontend/limits/SHOW_SPINNER';
export const UPDATE_MODAL = '@komigen/mobile-frontend/limits/MODAL';

const DEFAULT_MAX_LIMIT = 999999;

const initialState = {
	modal: false
};

const reducer = (state = initialState, { type, payload }) => {
	switch (type) {
		case GET_LIMITS:
		case INITIALIZE_DEPOSIT_LIMITS:
			return {
				...state,
				...payload
			};
		case GET_DEPOSIT_LIMITS:
			return {
				...state,
				...payload
			};
		case SHOW_SPINNER:
			return {
				...state,
				...payload
			};
		case EDIT_LIMIT:
			return {
				...state,
				currentlyEditedLimit: {
					type: payload.type ? payload.type : state.currentlyEditedLimit.type,
					freq: payload.freq ? payload.freq : state.currentlyEditedLimit.freq,
					amount: payload.amount
				}
			};
		case UPDATE_LIMIT:
			return {
				...state,
				currentlyEditedLimit: {}
			};
		case REMOVE_LIMIT:
		case REMOVE_ALL_LIMITS:
		case REMOVE_LIMIT_UPDATE_REQUEST:
			return state;
		case RESET_LIMITS:
			return {
				...state,
				limits: null,
				deposit: null,
				...payload
			};
		case GET_REALITY_CHECK_DURATION:
			return {
				...state,
				...payload
			};
		case UPDATE_DEPOSIT_LIMIT:
			return {
				...state,
				...payload
			};
		case SHOW_LOSS_LIMIT_MESSAGE:
			return {
				...state,
				lossLimitMessage: {
					show: payload.show,
					timeUnit: payload.timeUnit,
					maxPlayableAmount: payload.maxPlayableAmount,
					currency: payload.currency
				}
			};
		case UPDATE_MODAL: {
			return {
				...state,
				modal: payload
			};
		}
		default:
			return state;
	}
};

export function updateModal(payload = false) {
	return dispatch => dispatch({ type: UPDATE_MODAL, payload });
}

function timeConstToFreq(timeConst) {
	switch (timeConst) {
		case 5:
			return 'day';
		case 6:
			return 'week';
		case 7:
			return 'month';
		default:
			return undefined;
	}
}

// get timestamp of the remaining time in milliseconds
const getRemainingTimeStamp = (hours, minutes, seconds) => {
	// initialize in ms
	let remainingTime = 0;
	// convert hrs to ms and add it
	remainingTime += hours ? hours * 60 * 60 * 1000 : 0;
	// convert minutes to ms and add it
	remainingTime += minutes ? minutes * 60 * 1000 : 0;
	// convert seconds to ms and add it
	remainingTime += seconds ? seconds * 1000 : 0;
	return Date.now() + remainingTime;
};

const getFormattedDate = (d = '') => {
	const now = new Date(d);
	return now.toLocaleDateString('de-DE', {
		weekday: 'long',
		year: 'numeric',
		month: 'long',
		day: 'numeric',
		hour: 'numeric',
		minute: 'numeric'
	});
};

/*
	Incomplete Limit Arr - Incomplete limit array returned from backend
	Limit Type - We can't always deduce the type of the limit array if the returned array from backend is empty, therefore we need this to be explicitly declared
	Freq Arr - Array of frequencies of limits we want to fill. Fills for all frequencies by default

	Returns the limit data filled by placeholder data, ordered by the declared frequency order.
*/
function fillAndOrderLimitsArray(
	incompleteLimitArr,
	limitType,
	disabledFreqArr = [] // ['day', 'week', 'month']
) {
	//	creating custom deposit limit array based on disabled periods
	const customFreqArr = [5, 6, 7].filter(period => {
		return !disabledFreqArr.includes(period);
	});

	const placeholderArr = customFreqArr.map(value => {
		return {
			type: limitType,
			freq: timeConstToFreq(value),
			amount: '',
			applicableLimitAmount: '',
			remainingLimitAmount: '',
			pendingAmount: '',
			createdTimeStamp: '',
			updatedTimeStamp: '',
			remainingDay: '',
			remainingHour: '',
			remainingMinute: '',
			hoursUntilLimit: '',
			minutesUntilLimit: '',
			wagerUntilLimit: ''
		};
	});

	incompleteLimitArr.forEach(realLimit => {
		const placeholderLimitIndex = placeholderArr.findIndex(placeholderLimit => {
			return placeholderLimit.freq === timeConstToFreq(realLimit.timeConst);
		});
		const remainingTime = getRemainingTimeStamp(
			realLimit.hoursUntilLimit,
			realLimit.minutesUntilLimit,
			realLimit.secondsUntilLimit
		);

		let updateTime = new Date();
		updateTime = date.addDays(updateTime, Math.max(realLimit.day, 0));
		updateTime = date.addHours(updateTime, Math.max(realLimit.hour, 0));
		updateTime = date.addMinutes(updateTime, Math.max(realLimit.minute, 0));

		placeholderArr[placeholderLimitIndex] = {
			activeTime: realLimit.activeTime,
			amount: realLimit.limit,
			applicableLimitAmount: realLimit.applicableLimitAmount,
			createdTimeStamp: realLimit.created,
			freq: timeConstToFreq(realLimit.timeConst),
			hoursUntilLimit: realLimit.hoursUntilLimit,
			minutesUntilLimit: realLimit.minutesUntilLimit,
			pendingAmount: realLimit.pendingLimit,
			remainingDay: Math.max(realLimit.day, 0),
			remainingHour: Math.max(realLimit.hour, 0),
			remainingLimitAmount: realLimit.remainingLimitAmount,
			remainingMinute: Math.max(realLimit.minute, 0),
			remainingTime,
			secondsUntilLimit: realLimit.secondsUntilLimit,
			type: limitType,
			updateTime: updateTime.toString(),
			updatedTimeStamp: realLimit.updated,
			wagerUntilLimit: realLimit.wagerUntilLimit
		};
	});

	return placeholderArr;
}

const getRemainingTime = activeTime => {
	let days = 0;
	let hours = 0;
	let minutes = 0;
	const currentTime = Date.now();

	if (!activeTime || activeTime <= currentTime) {
		return [days, hours, minutes];
	}

	let delta = Math.abs(activeTime - currentTime) / 1000;

	days = Math.floor(delta / 86400);
	delta -= days * 86400;

	// calculate (and subtract) whole hours
	hours = Math.floor(delta / 3600) % 24;
	delta -= hours * 3600;

	// calculate (and subtract) whole minutes
	minutes = Math.floor(delta / 60) % 60;
	delta -= minutes * 60;

	return [days, hours, minutes];
};

const formatBalanceLimit = (balance = {}) => {
	const [days, hours, minutes] = getRemainingTime(balance?.activeTime);

	return [
		{
			activeTime: balance?.activeTime,
			type: 'balance',
			freq: 'amount',
			amount: balance?.limitAmount,
			createdTimeStamp: balance?.created,
			updatedTimeStamp: balance?.updated,
			pendingAmount: balance?.pendingLimitAmount,
			remainingDay: days,
			remainingHour: hours,
			remainingMinute: minutes,
			remainingTime: balance?.activeTime,
			updateTime: new Date(balance?.activeTime).toString()
		}
	];
};

function getLimits() {
	return async dispatch => {
		let loaded = false;
		const response = await requestApi('/player/limit');
		if (response.status === 'SUCCESS') {
			let { deposit, wager, time, spend, loss, balance } = response.result;
			loaded = true;
			const { disabledPeriods } = response.result;

			deposit =
				deposit &&
				fillAndOrderLimitsArray(deposit, 'deposit', disabledPeriods ? disabledPeriods.DEPOSIT : undefined);
			wager =
				wager && fillAndOrderLimitsArray(wager, 'wager', disabledPeriods ? disabledPeriods.WAGER : undefined);
			spend =
				spend && fillAndOrderLimitsArray(spend, 'spend', disabledPeriods ? disabledPeriods.SPEND : undefined);

			time = time && fillAndOrderLimitsArray(time, 'time', disabledPeriods ? disabledPeriods.TIME : undefined);

			loss = loss && fillAndOrderLimitsArray(loss, 'loss', disabledPeriods ? disabledPeriods.LOSS : undefined);

			balance = formatBalanceLimit(balance ?? {});

			const payload = {
				...response.result,
				deposit,
				wager,
				time,
				spend,
				loss,
				loaded,
				balance
			};

			dispatch({
				type: GET_LIMITS,
				payload
			});
		}
	};
}

function fillDepositLimitsArray(limitType, enabledFreqArr, prefillAmount) {
	//	creating custom deposit limit array based on enabled periods
	return enabledFreqArr.map(value => {
		return {
			type: limitType,
			freq: timeConstToFreq(value),
			amount: prefillAmount,
			applicableLimitAmount: '',
			remainingLimitAmount: '',
			pendingAmount: '',
			createdTimeStamp: '',
			updatedTimeStamp: '',
			remainingDay: '',
			remainingHour: '',
			remainingMinute: '',
			hoursUntilLimit: '',
			minutesUntilLimit: '',
			wagerUntilLimit: ''
		};
	});
}

function createDepositLimitStructure(enabledFreqArr, prefillAmount) {
	return async (dispatch, getState) => {
		let {
			limits: { deposit }
		} = getState();

		if (!deposit) {
			deposit = fillDepositLimitsArray('deposit', enabledFreqArr, prefillAmount);
		}
		const payload = { deposit };
		dispatch({
			type: INITIALIZE_DEPOSIT_LIMITS,
			payload
		});
	};
}

function getDepositLimits() {
	return async dispatch => {
		try {
			const response = await requestApi('/player/limit/getDepositLimits');
			if (response.status === 'SUCCESS') {
				let deposit = response.result;
				deposit = fillAndOrderLimitsArray(deposit, 'deposit');
				const payload = { deposit };
				dispatch({
					type: GET_DEPOSIT_LIMITS,
					payload
				});
			}
		} catch (e) {
			console.warn('Get Deposit Limits Error!', e); // eslint-disable-line
		}
	};
}

function getRealityCheckDuration() {
	return async dispatch => {
		const response = await requestApi('/player/limits/reality-check/duration', 'GET');

		if (response.status === 'SUCCESS') {
			const payload = response.result;

			dispatch({
				type: GET_REALITY_CHECK_DURATION,
				payload
			});
		}
	};
}

const limitFreqText = {
	day: 'Daily',
	week: 'Weekly',
	month: 'Monthly'
};

const freqEnum = {
	day: 1,
	week: 2,
	month: 3
};

const dataLayerLimitUpdate = (freq, type, value = '', isAccount = false, isNetherlandsRegulatedFranchise) => {
	if (isNetherlandsRegulatedFranchise) {
		// Use dynamically extracted keys
		const freqLabel = Object.keys(limitFreqText).includes(freq) ? `${limitFreqText[freq]} - ` : '';

		insertTrackEvent({
			category: 'Sidebar',
			action: 'Limit apply',
			label: `${freqLabel}${type}${isAccount ? ' - account' : ''}`,
			value
		});
	} else {
		insertTrackEvent({
			category: 'Sidebar',
			action: 'Limit setup',
			label: `${limitFreqText[freq]} - ${type}${isAccount ? ' - account - manual' : ''}`,
			value
		});
	}
};

function generateSingularPluralText(number, singularText, pluralText) {
	return number > 1 ? `${pluralText}` : `${singularText}`;
}

function genericLimitValidation(limitsArr, limitObj, checkLowerLimits, checkHigherLimits) {
	const lowerLimits = [];
	const higherLimits = [];
	limitsArr.forEach(limit => {
		if (freqEnum[limit.freq] > freqEnum[limitObj.freq]) {
			higherLimits.push(limit);
		} else if (freqEnum[limit.freq] < freqEnum[limitObj.freq]) {
			lowerLimits.push(limit);
		}
	});

	// never forget to reverse the lower limits array
	// or it will start validating from lowest limit to highest
	// whereas we want to validate against the closest limit first
	const errorContent = checkLowerLimits(limitObj, lowerLimits.reverse()) || checkHigherLimits(limitObj, higherLimits);

	if (errorContent) {
		return errorContent;
	}
	return true;
}

function doTimeLimitsValidation(limitObj, limitsArr, timeLimitBounds = {}) {
	function checkLowerLimits(limitToValidate, lowerLimitsArr) {
		let errorContent = {};
		const lowerLimitResult = lowerLimitsArr.every(lowerLimit => {
			if (!lowerLimit.amount) return true;
			errorContent = {
				errorTextKey: `MOBILE_RESPONSIBLE_LIMIT_ERROR_${limitToValidate.freq.toUpperCase()}_CAN_NOT_BE_SMALLER_THAN_${lowerLimit.freq.toUpperCase()}`,
				errorAmount: lowerLimit.amount,
				errorAmountUnit: generateSingularPluralText(
					lowerLimit.amount,
					'REALITY_CHECK_HOUR',
					'REALITY_CHECK_HOURS'
				)
			};
			// this checks if the amount being set for the frequency is higher than
			// all the amounts of lower frequencies
			// we throw an error and break out of every loop if its not higher
			return limitToValidate.amount >= lowerLimit.amount;
		});

		return !lowerLimitResult ? errorContent : false;
	}

	function checkHigherLimits(limitToValidate, higherLimitsArr) {
		let errorContent = {};
		const higherLimitResult = higherLimitsArr.every(higherLimit => {
			if (!higherLimit.amount) return true;
			errorContent = {
				errorTextKey: `MOBILE_RESPONSIBLE_LIMIT_ERROR_${limitToValidate.freq.toUpperCase()}_CAN_NOT_BE_LARGER_THAN_${higherLimit.freq.toUpperCase()}`,
				errorAmount: higherLimit.amount,
				errorAmountUnit: generateSingularPluralText(
					higherLimit.amount,
					'REALITY_CHECK_HOUR',
					'REALITY_CHECK_HOURS'
				)
			};
			// this checks if the amount being set for the frequency is lower than
			// all the amounts of higher frequencies
			// we throw an error and break out of every loop if it's not lower
			return limitToValidate.amount <= higherLimit.amount;
		});

		return !higherLimitResult ? errorContent : false;
	}

	const validationMessage = {};
	const genericValidation = genericLimitValidation(limitsArr, limitObj, checkLowerLimits, checkHigherLimits);
	if (genericValidation !== false && genericValidation.errorTextKey) {
		return genericValidation;
	}

	if (limitObj.freq === 'day') {
		if (Object.keys(timeLimitBounds).length > 0) {
			validationMessage.errorTextKey =
				limitObj.amount >= timeLimitBounds?.day?.minLimit && limitObj.amount <= timeLimitBounds?.day?.maxLimit
					? ''
					: 'MOBILE_RESPONSIBLE_LIMIT_ERROR_TIME_DAILY_LIMIT_OUT_OF_BOUNDS';
		}
	}

	if (limitObj.freq === 'week') {
		if (Object.keys(timeLimitBounds).length > 0) {
			validationMessage.errorTextKey =
				limitObj.amount >= timeLimitBounds?.week?.minLimit && limitObj.amount <= timeLimitBounds?.week?.maxLimit
					? ''
					: 'MOBILE_RESPONSIBLE_LIMIT_ERROR_TIME_WEEKLY_LIMIT_OUT_OF_BOUNDS';
		}
	}

	if (limitObj.freq === 'month') {
		if (Object.keys(timeLimitBounds).length > 0) {
			validationMessage.errorTextKey =
				limitObj.amount >= timeLimitBounds?.month?.minLimit &&
				limitObj.amount <= timeLimitBounds?.month?.maxLimit
					? ''
					: 'MOBILE_RESPONSIBLE_LIMIT_ERROR_TIME_MONTHLY_LIMIT_OUT_OF_BOUNDS';
		}
	}

	if (validationMessage.errorTextKey) {
		return validationMessage;
	}

	return true;
}

// used only for wager and loss limits
function doLimitsValidation(limitObj, limitsArr, currency, locale, license, amountLimit = 0) {
	function isValidAmount(limit, amount) {
		if (amount !== 0) {
			if (license === LICENSE_OGA) {
				return limit.amount >= 0 && limit.amount <= amount;
			}
			return limit.amount > 0 && limit.amount <= amount;
		}
		if (license === LICENSE_OGA) {
			return limit.amount.length <= 11 && Number.parseInt(limit.amount, 10) >= 0;
		}
		return limit.amount.length <= 11 && parseInt(limit.amount, 10) > 0;
	}
	function checkLowerLimits(limitToValidate, lowerLimitsArr) {
		let errorContent = {};
		const lowerLimitResult = lowerLimitsArr.every(lowerLimit => {
			if (!lowerLimit.amount) return true;
			// we set this to the current amount if there is no pending amount so that it is easier to evaluate the bottom statement to true/false
			const pendingAmount = lowerLimit.pendingAmount > 0 ? lowerLimit.pendingAmount : lowerLimit.amount;
			errorContent = {
				errorTextKey: `MOBILE_RESPONSIBLE_LIMIT_ERROR_${limitToValidate.freq.toUpperCase()}_CAN_NOT_BE_SMALLER_THAN_${lowerLimit.freq.toUpperCase()}`,
				errorAmount: currencyFormatter.format(Math.max(lowerLimit.amount, pendingAmount), currency, locale)
			};
			// this checks if the amount being set for the frequency is higher than
			// all the amounts of lower frequencies
			// we throw an error and break out of every loop if its not higher
			return limitToValidate.amount >= lowerLimit.amount && limitToValidate.amount >= pendingAmount;
		});

		return !lowerLimitResult ? errorContent : false;
	}

	function checkHigherLimits(limitToValidate, higherLimitsArr) {
		let errorContent = {};
		const higherLimitResult = higherLimitsArr.every(higherLimit => {
			if (!higherLimit.amount) return true;
			// we set this to the current amount if there is no pending amount so that it is easier to evaluate the bottom statement to true/false
			const pendingAmount = higherLimit.pendingAmount > 0 ? higherLimit.pendingAmount : higherLimit.amount;
			errorContent = {
				errorTextKey: `MOBILE_RESPONSIBLE_LIMIT_ERROR_${limitToValidate.freq.toUpperCase()}_CAN_NOT_BE_LARGER_THAN_${higherLimit.freq.toUpperCase()}`,
				errorAmount: currencyFormatter.format(Math.min(higherLimit.amount, pendingAmount), currency, locale)
			};
			// this checks if the amount being set for the frequency is lower than
			// all the amounts of higher frequencies
			// we throw an error and break out of every loop if its not lower
			return limitToValidate.amount <= higherLimit.amount && limitToValidate.amount <= pendingAmount;
		});

		return !higherLimitResult ? errorContent : false;
	}

	if (isValidAmount(limitObj, amountLimit)) {
		return genericLimitValidation(limitsArr, limitObj, checkLowerLimits, checkHigherLimits);
	}
	return {
		errorTextKey: 'MOBILE_RESPONSIBLE_LIMIT_ERROR_LIMIT_OUT_OF_BOUNDS'
	};
}

const showSpinner = payload => ({
	type: SHOW_SPINNER,
	payload
});

function closeModal() {
	return dispatch => {
		dispatch(updateModal(false));
		dispatch(hideBinaryNotification());
	};
}

function updateLimit(withinSidebar, depositLimitMax = null, timeLimitBounds = {}, config = {}) {
	return async (dispatch, getState) => {
		// TODO update/refactor validations (deposit, time, wager, spend, loss)

		const state = getState();
		const { limits, account, brand } = state;

		const {
			license,
			isGermanRegulatedFranchise,
			isGermanSportsBookRegulatedFranchise,
			franchiseName,
			isNetherlandsRegulatedFranchise
		} = brand;
		const { currentlyEditedLimit } = limits;
		const limitAmount = Number.parseInt(currentlyEditedLimit.amount, 10);
		const { id: encryptedPlayerId, currency, locale } = account || {};
		let response = {};

		const { deposit: depositConstraints, balance: balanceConstraints } = config;

		if (
			limits[currentlyEditedLimit.type].find(lim => lim.freq === currentlyEditedLimit.freq)?.amount ===
			parseInt(currentlyEditedLimit?.amount, 10)
		) {
			// Kostas want to track even when value is not changed
			dataLayerLimitUpdate(
				currentlyEditedLimit.freq,
				currentlyEditedLimit.type,
				limitAmount,
				true,
				isNetherlandsRegulatedFranchise
			);

			dispatch(closeModal());
			return false;
		}
		let validationResult = false;
		if (currentlyEditedLimit.type === 'time') {
			validationResult = doTimeLimitsValidation(
				currentlyEditedLimit,
				limits[currentlyEditedLimit.type],
				timeLimitBounds
			);
		} else if (currentlyEditedLimit.type === 'wager') {
			validationResult = doLimitsValidation(
				currentlyEditedLimit,
				limits[currentlyEditedLimit.type],
				account.currency,
				account.locale,
				license
			);
		} else if (currentlyEditedLimit.type === 'deposit') {
			const depositMin = Number(depositConstraints?.min || 1);

			if (Number.isNaN(limitAmount)) {
				validationResult = {
					errorTextKey: 'MOBILE_RESPONSIBLE_LIMIT_ERROR_LIMIT_FIELD_EMPTY'
				};
			} else if (limitAmount < depositMin) {
				validationResult = {
					errorTextKey: 'MOBILE_RESPONSIBLE_GAMING_LIMIT_MIN_REQUIRED',
					errorAmount: currencyFormatter.format(depositMin, currency, locale, true)
				};
			} else if (
				limitAmount <= 0 &&
				(isGermanRegulatedFranchise || isGermanSportsBookRegulatedFranchise || isNetherlandsRegulatedFranchise)
			) {
				validationResult = {
					errorTextKey: 'MOBILE_RESPONSIBLE_LIMIT_ERROR_LIMIT_OUT_OF_BOUNDS'
				};
			} else if (limitAmount > depositLimitMax) {
				validationResult = {
					errorTextKey: 'MOBILE_RESPONSIBLE_LIMIT_ERROR_EXCEEDS_INPUT'
				};
			} else {
				validationResult = true;
			}
		} else if (currentlyEditedLimit.type === 'spend') {
			if (Number.isNaN(limitAmount)) {
				validationResult = {
					errorTextKey: 'MOBILE_RESPONSIBLE_LIMIT_ERROR_LIMIT_FIELD_EMPTY'
				};
			} else if (limitAmount < 0) {
				validationResult = {
					errorTextKey: 'MOBILE_RESPONSIBLE_LIMIT_ERROR_LIMIT_OUT_OF_BOUNDS'
				};
			} else {
				validationResult = true;
			}
		} else if (currentlyEditedLimit.type === 'loss') {
			// TODO dedicated loss limits validation
			validationResult = doLimitsValidation(
				currentlyEditedLimit,
				limits[currentlyEditedLimit.type],
				account.currency,
				account.locale,
				license
			);
		} else if (currentlyEditedLimit.type === 'balance') {
			let minBalance = 1;
			let maxBalance = DEFAULT_MAX_LIMIT;

			if (isNetherlandsRegulatedFranchise) {
				minBalance = Number(balanceConstraints.min || 1);
				maxBalance = Number(balanceConstraints.max || DEFAULT_MAX_LIMIT);
			}

			if (Number.isNaN(limitAmount)) {
				validationResult = {
					errorTextKey: 'MOBILE_RESPONSIBLE_LIMIT_ERROR_LIMIT_FIELD_EMPTY'
				};
			} else if (limitAmount < minBalance) {
				// Track event for min limit validation failure
				if (isNetherlandsRegulatedFranchise) {
					const formattedAttempted = currencyFormatter.format(limitAmount, currency, locale, true);
					const formattedMin = currencyFormatter.format(minBalance, currency, locale, true);

					insertTrackEvent({
						category: 'Sidebar',
						action: 'Limit validation',
						label: `min limit not met (attempted: ${formattedAttempted}, min: ${formattedMin}) - balance - account`,
						value: limitAmount
					});
				}

				validationResult = {
					errorTextKey: 'MOBILE_RESPONSIBLE_GAMING_LIMIT_MIN_REQUIRED',
					errorAmount: currencyFormatter.format(minBalance, currency, locale, true)
				};
			} else if (limitAmount > maxBalance) {
				// Track event for max limit validation failure
				if (isNetherlandsRegulatedFranchise) {
					const formattedAttempted = currencyFormatter.format(limitAmount, currency, locale, true);
					const formattedMax = currencyFormatter.format(maxBalance, currency, locale, true);

					insertTrackEvent({
						category: 'Sidebar',
						action: 'Limit validation',
						label: `max limit exceeded (attempted: ${formattedAttempted}, max: ${formattedMax}) - balance - account`,
						value: limitAmount
					});
				}

				validationResult = {
					message: `${getState()
						.translations.MOBILE_RESPONSIBLE_BALANCE_LIMIT_MAX_REQUIRED.replace(
							'__min__',
							currencyFormatter.format(minBalance, currency, undefined, true)
						)
						.replace('__max__', currencyFormatter.format(maxBalance, currency, undefined, true))}`
				};
			} else {
				validationResult = true;
			}
		}

		if (validationResult === true) {
			const germanSharedLimit = isGermanRegulatedFranchise && currentlyEditedLimit.freq === 'shared';
			try {
				if (germanSharedLimit) {
					const payload = { franchiseName, encryptedPlayerId, ...currentlyEditedLimit };
					response = await requestApi(
						'/lugas-service/public/player/deposit/set-new-lugas-limit',
						payload,
						'POST'
					);
				} else {
					response = await requestApi('/player/limit', currentlyEditedLimit, 'POST');
				}
			} catch (error) {
				dispatch(
					showNotification({
						message: getState().translations.PLAYER_EXCEPTION_GENERAL_ERROR,
						color: 'red',
						autoHide: true,
						openInSidebar: withinSidebar
					})
				);
				// eslint-disable-next-line no-console
				console.log(error);
			}
			// call the action
			dispatch({
				type: UPDATE_LIMIT
			});

			dataLayerLimitUpdate(
				currentlyEditedLimit.freq,
				currentlyEditedLimit.type,
				limitAmount,
				true,
				isNetherlandsRegulatedFranchise
			);

			// remove the binary question after you get a response
			dispatch(closeModal());
			// show response on notification
			if (response?.status === 'SUCCESS') {
				let messageTextkey;
				let previousLimit;
				let coolOffdate;
				// Notify Native apps on successful time limit update
				sendAppBridgeEvent(WEB_EVENTS.PLAYER_LIMITS_UPDATED);
				if (germanSharedLimit) {
					const { coolOffInfo } = response;
					coolOffdate = coolOffInfo ? getFormattedDate(coolOffInfo) : false;
					messageTextkey = coolOffInfo
						? 'MOBILE_RESPONSIBLE_LIMIT_UPDATE_DELAY'
						: 'MOBILE_RESPONSIBLE_LIMIT_SHARED_UPDATE_SUCCESS';
				} else {
					previousLimit = limits[currentlyEditedLimit.type].find(limitItem => {
						return limitItem.freq === currentlyEditedLimit.freq;
					});

					messageTextkey =
						previousLimit && previousLimit.amount && previousLimit.amount <= currentlyEditedLimit.amount
							? 'MOBILE_RESPONSIBLE_LIMIT_UPDATE_DELAY'
							: 'MOBILE_RESPONSIBLE_LIMIT_UPDATE_SUCCESS';
				}

				let message = getState().translations[messageTextkey];
				if (coolOffdate && germanSharedLimit) {
					message += coolOffdate;
				}

				dispatch(
					showNotification({
						message,
						color: messageTextkey === 'MOBILE_RESPONSIBLE_LIMIT_UPDATE_DELAY' ? 'neutral' : 'green',
						autoHide: true,
						openInSidebar: withinSidebar
					})
				);
				if (
					!(previousLimit && previousLimit.amount) ||
					(previousLimit && previousLimit.amount && previousLimit.amount > currentlyEditedLimit.amount)
				) {
					if (currentlyEditedLimit.type === 'time') {
						dispatch(timeLimitRefetch());
					}
				}
			} else {
				dispatch(
					showNotification({
						message: response?.errorDescription,
						color: 'red',
						autoHide: true,
						openInSidebar: withinSidebar
					})
				);
			}
			// get the latest limit info
			dispatch(getLimits());
		} else {
			const errorMessage = validationResult.message
				? validationResult.message
				: `${getState().translations[validationResult.errorTextKey]} ${
						validationResult.errorAmount ? validationResult.errorAmount : ''
					} ${
						validationResult.errorAmountUnit
							? getState().translations[validationResult.errorAmountUnit]
							: ''
					}`;
			// show response on notification
			dispatch(
				showNotification({
					message: errorMessage,
					color: 'red',
					autoHide: true,
					openInSidebar: withinSidebar
				})
			);
		}
	};
}

function updateDepositLimit() {
	return async (dispatch, getState) => {
		const { currentlyEditedLimit } = getState().limits;
		const { deposit } = getState().limits;
		deposit.forEach(depositLimit => {
			if (depositLimit?.freq === currentlyEditedLimit.freq) {
				// eslint-disable-next-line no-param-reassign
				depositLimit.amount = Number.parseInt(currentlyEditedLimit.amount, 10);
			}
		});
		// call the action
		dispatch({
			type: UPDATE_DEPOSIT_LIMIT,
			payload: { limit: { deposit, currentlyEditedLimit: {} } }
		});

		dataLayerLimitUpdate(currentlyEditedLimit.freq, 'deposit', currentlyEditedLimit.amount);

		// remove the binary question after you get a response
		dispatch(closeModal());

		return true;
	};
}

function autoPopulateLimit(data) {
	return async (dispatch, getState) => {
		const { deposit } = getState().limits;
		const { amount, maxInput, weeklyMultiplier, monthlyMultiplier } = data;
		deposit.forEach(depositLimit => {
			if (depositLimit.freq === DL_TYPE_DAY) {
				depositLimit.amount = amount > maxInput ? maxInput : Math.round(amount); // eslint-disable-line
			}
			if (depositLimit.freq === DL_TYPE_WEEK) {
				const newAmount = amount * weeklyMultiplier;
				depositLimit.amount = newAmount > maxInput ? maxInput : Math.round(newAmount); // eslint-disable-line
			}
			if (depositLimit.freq === DL_TYPE_MONTH) {
				const newAmount = amount * monthlyMultiplier;
				depositLimit.amount = newAmount > maxInput ? maxInput : Math.round(newAmount); // eslint-disable-line
			}
		});

		dispatch({
			type: UPDATE_DEPOSIT_LIMIT,
			payload: { limit: { deposit } }
		});

		return true;
	};
}

function editLimit(payload) {
	return {
		type: EDIT_LIMIT,
		payload
	};
}

function resetLimits(payload) {
	return {
		type: RESET_LIMITS,
		payload
	};
}

function showLimitEditPopup(payload) {
	// edit currently edited limit object in state
	return dispatch => {
		// make sure the amount is not a float

		dispatch(editLimit(payload.limitData));
		// setup the binary question component
		/* dispatch(
			showBinaryQuestion({
				...payload.notificationData,
				inputValue: getState().limits.currentlyEditedLimit.amount
			})
		); */
	};
}

function updateLimitEditPopup(payload) {
	return dispatch => {
		// edit currently edited limit objects value
		// make sure the amount is not a float

		dispatch(editLimit(payload.limitData));
	};
}

function removeLimit(payload, withinSidebar) {
	return async (dispatch, getState) => {
		const response = await requestApi(
			'/player/limit',
			{
				type: payload.type,
				freq: payload.freq
			},
			'DELETE'
		);

		if (response.status === 'SUCCESS') {
			dispatch(
				showNotification({
					message: getState().translations.MOBILE_RESPONSIBLE_LIMIT_REMOVE_REQUEST_SUCCESS,
					color: 'neutral',
					autoHide: true,
					openInSidebar: withinSidebar
				})
			);
			dispatch(hideBinaryNotification());
		} else {
			dispatch(
				showNotification({
					message: response.errorDescription,
					color: 'red',
					autoHide: true,
					openInSidebar: withinSidebar
				})
			);
		}

		dispatch(getLimits());
	};
}

function removeAllLimits(payload, withinSidebar) {
	return async (dispatch, getState) => {
		const response = await requestApi(
			'/player/limit/removeAllDeposit',
			{
				type: payload.type
			},
			'DELETE'
		);
		if (response.status === 'SUCCESS') {
			dispatch(
				showNotification({
					message: getState().translations.MOBILE_RESPONSIBLE_LIMIT_REMOVE_ALL_REQUEST_SUCCESS,
					color: 'neutral',
					autoHide: true,
					openInSidebar: withinSidebar
				})
			);
		} else {
			dispatch(
				showNotification({
					message: response.errorDescription,
					color: 'red',
					autoHide: true,
					openInSidebar: withinSidebar
				})
			);
		}
		dispatch(hideBinaryNotification());
		dispatch(getLimits());
	};
}

function removeLimitUpdateRequest(limit, withinSidebar) {
	return async (dispatch, getState) => {
		const response = await requestApi(
			'/player/limit/pending',
			{
				type: limit.type,
				freq: limit.freq
			},
			'DELETE'
		);

		if (response.status === 'SUCCESS') {
			dispatch(
				showNotification({
					message: getState().translations.MOBILE_RESPONSIBLE_LIMIT_PENDING_REMOVE_REQUEST_SUCCESS,
					color: 'green',
					autoHide: true,
					openInSidebar: withinSidebar
				})
			);
			dispatch(hideBinaryNotification());
		} else {
			dispatch(
				showNotification({
					message: response.errorDescription,
					color: 'red',
					autoHide: true,
					openInSidebar: withinSidebar
				})
			);
		}
		dispatch(getLimits());
	};
}

function showLossLimitMessage({ show, timeUnit, maxPlayableAmount, currency }) {
	return {
		type: SHOW_LOSS_LIMIT_MESSAGE,
		payload: {
			show,
			timeUnit,
			maxPlayableAmount,
			currency
		}
	};
}

reducerRegistry.register(REDUCER_NAME, reducer);
export {
	getLimits,
	getDepositLimits,
	updateLimit,
	updateDepositLimit,
	autoPopulateLimit,
	editLimit,
	showLimitEditPopup,
	updateLimitEditPopup,
	removeLimit,
	removeAllLimits,
	removeLimitUpdateRequest,
	getRealityCheckDuration,
	createDepositLimitStructure,
	resetLimits,
	showLossLimitMessage,
	showSpinner
};
export default reducer;
